/**
 * The tenant admins' Licences page and the seat check at Teslyn Cloud sign-in (MB decisions 289, 290, 295, 296, 299,
 * 447, 838; docs/specs/cloud/licences-page.md). The rules the web app shows: who sees the page, what the web check's
 * answer means for the person signing in, the seat and device counts and their words. What anyone may read or
 * release is decided by RLS and the licence functions (20261020060000, 20261028150000); this module only explains.
 */
import {
  daysSince,
  LICENCE_DEVICE_PLATFORMS,
  type LicenceDeviceKind,
  type LicenceDevicePlatform,
} from './licence.js';

/** Who sees the Licences page and releases seats and devices (decision 295): tenant admins only. */
export const LICENCE_PAGE_ROLES: readonly string[] = ['tenant_admin'];

/** Whether any of `roles` opens the Licences page. Display only: the policies and licence_may_end decide. */
export function canManageLicences(roles: readonly string[]): boolean {
  return roles.some((r) => LICENCE_PAGE_ROLES.includes(r));
}

/** The platform setting that switches the seat check at sign-in on (`licence_web_check`): 'on', or null for off. */
export const LICENCE_WEB_CHECK_KEY = 'licence_web_check';

/** Whether the stored value means on. Anything but exactly 'on' (null, a missing row) is off. */
export function webCheckOn(value: string | null | undefined): boolean {
  return value === 'on';
}

// ---------------------------------------------------------------- the seat check at sign-in (licences spec §6.4)

/** A seat holder in the "no free seat" answer, with the whole days since their last check-in (decision 296). */
export interface WebSeatHolder {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly lastCheckinAt: string | null;
  readonly daysSinceCheckin: number | null;
}

/** What `licence_web_checkin(tenant)` answers. */
export type WebCheckinAnswer =
  | { readonly status: 'off' }
  | { readonly status: 'active'; readonly seatId: string; readonly subscriptionEnds: string | null }
  | { readonly status: 'not_needed' | 'not_member' | 'no_licence' | 'tenant_cancelled' | 'phone_only' }
  | {
      readonly status: 'no_seat';
      readonly seats: number;
      readonly inUse: number;
      readonly holders: readonly WebSeatHolder[];
    };

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const PLAIN = ['off', 'not_needed', 'not_member', 'no_licence', 'tenant_cancelled', 'phone_only'] as const;

/** The database's answer, checked; null for anything else (an error, an older database, a proxy page). */
export function parseWebCheckinAnswer(body: unknown, now: Date): WebCheckinAnswer | null {
  if (!isRecord(body)) return null;
  const s = body.status;
  const plain = PLAIN.find((p) => p === s);
  if (plain) return { status: plain };
  if (s === 'active') {
    const ends = body.subscription_ends ?? null;
    if (typeof body.seat_id !== 'string' || (ends !== null && typeof ends !== 'string')) return null;
    return { status: 'active', seatId: body.seat_id, subscriptionEnds: ends };
  }
  if (s === 'no_seat') {
    if (!isCount(body.seats) || !isCount(body.in_use) || !Array.isArray(body.holders)) return null;
    const holders: WebSeatHolder[] = [];
    for (const h of body.holders as unknown[]) {
      if (!isRecord(h) || typeof h.user_id !== 'string') return null;
      const at = typeof h.last_checkin_at === 'string' ? h.last_checkin_at : null;
      holders.push({
        userId: h.user_id,
        name: typeof h.name === 'string' && h.name.trim() ? h.name : 'Someone',
        email: typeof h.email === 'string' ? h.email : '',
        lastCheckinAt: at,
        daysSinceCheckin: daysSince(at, now),
      });
    }
    return { status: 'no_seat', seats: body.seats, inUse: body.in_use, holders };
  }
  return null;
}

/**
 * What Teslyn Cloud does with a staff member signing in:
 * - `open`: let them in. The check is switched off, they hold or took a seat, or the check could not be made (an
 *   error or an answer Teslyn does not know): Teslyn never locks a person out because a check failed (as Teslyn
 *   Field, where no network is never an error).
 * - `no_seat`: every seat is taken (decision 296): show who holds them and the days since each last checked in; a
 *   tenant admin can release one.
 * - `ended`: the organisation has no licence, or it was cancelled (decision 865): staff are refused at sign-in;
 *   its tenant admins see only the Licences page's licence and invoices. Clients are never checked (decision 290).
 */
export type WebSignInGate =
  | { readonly kind: 'open'; readonly checked: boolean }
  | {
      readonly kind: 'no_seat';
      readonly seats: number;
      readonly inUse: number;
      readonly holders: readonly WebSeatHolder[];
    }
  | { readonly kind: 'ended'; readonly reason: 'no_licence' | 'tenant_cancelled' };

export function webSignInGate(answer: WebCheckinAnswer | null): WebSignInGate {
  if (answer === null) return { kind: 'open', checked: false };
  switch (answer.status) {
    case 'no_seat':
      return { kind: 'no_seat', seats: answer.seats, inUse: answer.inUse, holders: answer.holders };
    case 'no_licence':
    case 'tenant_cancelled':
      return { kind: 'ended', reason: answer.status };
    case 'active':
      return { kind: 'open', checked: true };
    case 'off':
    case 'not_needed':
    case 'not_member':
    case 'phone_only':
      // phone_only: a pilot-only sign-in (decision 917); Teslyn Cloud shows its pilot page before any check (access.ts)
      return { kind: 'open', checked: false };
  }
}

/** "3 days ago", "today", "never": whole days down, as the "no free seat" list and the Licences page say them. */
export function daysAgoText(days: number | null): string {
  if (days === null) return 'never';
  if (days <= 0) return 'today';
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

// ---------------------------------------------------------------- the Licences page

/** Seats in use against the licence (decision 299: lowering seats below use ends nobody). */
export interface SeatUsage {
  readonly seats: number;
  readonly inUse: number;
  readonly free: number;
  /** Seats in use beyond the licence; 0 when within it. */
  readonly over: number;
  readonly words: string;
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

export function seatUsage(seats: number, inUse: number): SeatUsage {
  const free = Math.max(0, seats - inUse);
  const over = Math.max(0, inUse - seats);
  const head = `${inUse} of ${plural(seats, 'seat')} in use`;
  const words =
    over > 0
      ? `${head}: ${over} more than your licence. Nobody is stopped, but nobody new can take a seat until ${over === 1 ? 'one is' : `${over} are`} released.`
      : free === 0
        ? `${head}: none free. Release one before someone new signs in.`
        : `${head}, ${free} free.`;
  return { seats, inUse, free, over, words };
}

/** One device on a seat, as the page reads it. */
export interface SeatDevice {
  readonly kind: LicenceDeviceKind;
  readonly platform: string | null;
}

/** The device's kind in words: "Laptop", "iPhone", "Android phone", "Phone". */
export function deviceLabel(kind: LicenceDeviceKind, platform: string | null): string {
  if (kind === 'laptop') {
    const os: Partial<Record<LicenceDevicePlatform, string>> = { macos: 'Mac', linux: 'Linux laptop' };
    return (platform && os[platform as LicenceDevicePlatform]) || 'Laptop';
  }
  if (platform === 'ios') return 'iPhone';
  if (platform === 'android') return 'Android phone';
  return 'Phone';
}

/** A seat's devices of each kind against what each seat covers (decision 289: 1 laptop and 2 mobiles). */
export interface SeatDeviceUse {
  readonly laptops: number;
  readonly laptopLimit: number;
  readonly mobiles: number;
  readonly mobileLimit: number;
  /** "Laptop 1 of 1 · Phones 0 of 2" */
  readonly words: string;
}

export function seatDeviceUse(
  devices: readonly SeatDevice[],
  limits: { readonly laptopsPerUser: number; readonly mobilesPerUser: number },
): SeatDeviceUse {
  const laptops = devices.filter((d) => d.kind === 'laptop').length;
  const mobiles = devices.filter((d) => d.kind === 'mobile').length;
  const l = `${limits.laptopsPerUser === 1 ? 'Laptop' : 'Laptops'} ${laptops} of ${limits.laptopsPerUser}`;
  const m = `${limits.mobilesPerUser === 1 ? 'Phone' : 'Phones'} ${mobiles} of ${limits.mobilesPerUser}`;
  return {
    laptops,
    laptopLimit: limits.laptopsPerUser,
    mobiles,
    mobileLimit: limits.mobilesPerUser,
    words: `${l} · ${m}`,
  };
}

// ---------------------------------------------------------------- pilot sign-ins (decision 918)

/**
 * Open seat rows split into the licence's seats and pilot-only sign-ins: a pilot-only sign-in's phones sit on a seat
 * row the database does not count (20261103100100 `takes_licence_seat`), so the page shows them apart.
 * `pilotOnly` holds the user ids whose only role in the organisation is Pilot.
 */
export function splitPilotSeats<T extends { readonly userId: string }>(
  seats: readonly T[],
  pilotOnly: ReadonlySet<string>,
): { readonly staff: readonly T[]; readonly pilots: readonly T[] } {
  return {
    staff: seats.filter((s) => !pilotOnly.has(s.userId)),
    pilots: seats.filter((s) => pilotOnly.has(s.userId)),
  };
}

/** "2 pilot sign-ins, 3 phones. Pilots take no seat; each may use 2 phones." */
export function pilotSeatWords(pilots: number, phones: number, mobilesPerUser: number): string {
  if (pilots === 0)
    return `No pilot sign-ins have a phone yet. Pilots take no seat; each may use ${plural(mobilesPerUser, 'phone')}.`;
  return `${plural(pilots, 'pilot sign-in')}, ${plural(phones, 'phone')}. Pilots take no seat; each may use ${plural(mobilesPerUser, 'phone')}.`;
}

/** The platforms a phone can report, for the page's help and tests (the phone app is batch 7). */
export const PHONE_PLATFORMS: readonly string[] = LICENCE_DEVICE_PLATFORMS.mobile;

/** What releasing does, in the confirmation's words (decisions 293, 295, 324). */
export function releaseWords(
  what:
    | { readonly kind: 'seat'; readonly person: string }
    | { readonly kind: 'device'; readonly device: string; readonly person: string },
): string {
  if (what.kind === 'device')
    return `Release ${what.device} of ${what.person}? The slot is free at once, so ${what.person} can set up a replacement. The old device becomes read only at its next start, once it has checked in.`;
  return `Release the seat of ${what.person}? Their laptop and phones are released with it and the seat is free for someone else at once. If ${what.person} signs in again they take a seat again while one is free.`;
}

/** Invoices not paid yet, per currency, largest first: what the Licences page and Braynewave's Invoicing show. */
export function invoicesOwing(
  invoices: readonly { readonly status: string; readonly currency: string; readonly amount: number }[],
): readonly (readonly [string, number])[] {
  const m = new Map<string, number>();
  for (const i of invoices) if (i.status === 'issued') m.set(i.currency, (m.get(i.currency) ?? 0) + i.amount);
  return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

// ---------------------------------------------------------------- switching the check on (Braynewave's console)

/** One organisation as Braynewave's Tenants page lists it, for the warnings before the check is switched on. */
export interface WebCheckTenant {
  readonly name: string;
  readonly status: string;
  /** null when Braynewave has recorded no licence. */
  readonly plan: string | null;
  readonly licenceStatus: string | null;
  readonly seats: number | null;
  readonly seatsInUse: number;
  readonly staff: number;
}

/**
 * Who would be refused once the seat check is switched on: an active organisation with staff but no licence, a
 * cancelled licence, or more staff than seats (those beyond the seats are refused at sign-in, decision 296). Shown
 * before Braynewave switches it on, so nobody is locked out by surprise.
 */
export function webCheckWarnings(tenants: readonly WebCheckTenant[]): string[] {
  const out: string[] = [];
  for (const t of tenants) {
    if (t.status !== 'active' || t.staff === 0) continue;
    const staff = plural(t.staff, 'staff member');
    if (t.plan === null || t.seats === null)
      out.push(`${t.name} has no licence: its ${staff} would be refused.`);
    else if (t.licenceStatus === 'cancelled')
      out.push(`${t.name}’s licence is cancelled: its ${staff} would be refused.`);
    else if (t.staff > t.seats)
      out.push(
        `${t.name} has ${staff} and ${plural(t.seats, 'seat')}: ${t.staff - t.seats} would be refused once the seats are taken.`,
      );
  }
  return out;
}
